import { useLocation } from '@gatsbyjs/reach-router';
import { useTheme } from '@mui/material/styles';
import type { MutableRefObject } from 'react';
import React, { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import * as ROUTES from '@constants/routes';

import { useIsMobile } from '@hooks/useIsMobile';
import { useSession } from '@hooks/useSession';

import DrawerSection from './DrawerSection';
import { SideDrawerStyle } from './SideDrawer.style';

const messages = defineMessages({
  iam: {
    id: 'admin.drawer.section.iam',
    defaultMessage: 'IAM',
  },
  system: {
    id: 'admin.drawer.section.system',
    defaultMessage: 'System',
  },
  users: {
    id: 'admin.drawer.users',
    defaultMessage: 'VITALL Users',
  },
  accounts: {
    id: 'admin.drawer.accounts',
    defaultMessage: 'Accounts',
  },
  integrations: {
    id: 'admin.drawer.integrations',
    defaultMessage: 'Integrations',
  },
  roles: {
    id: 'admin.drawer.roles',
    defaultMessage: 'Roles',
  },
  operations: {
    id: 'admin.drawer.operations',
    defaultMessage: 'Operations',
  },
  policies: {
    id: 'admin.drawer.policies',
    defaultMessage: 'Policies',
  },
  permissions: {
    id: 'admin.drawer.permissions',
    defaultMessage: 'Permissions',
  },
  configurations: {
    id: 'admin.drawer.configurations',
    defaultMessage: 'Configurations',
  },
});

interface AdminDrawerProps {
  isOpen?: boolean;
  onClose?: (event: MouseEvent) => void;
  btnRef?: MutableRefObject<HTMLButtonElement | null>;
}

function AdminDrawer({
  isOpen = false,
  onClose,
  btnRef = undefined,
}: AdminDrawerProps) {
  const { formatMessage } = useIntl();
  const {
    palette: { primary },
  } = useTheme();

  const drawerRef = useRef<HTMLInputElement>(null);
  const mobile = useIsMobile();
  const location = useLocation();

  const { pathname } = location;

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (
        isOpen &&
        onClose &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node) &&
        !btnRef?.current?.contains(event.target as Node)
      ) {
        onClose(event);
      }
    }

    document.addEventListener('mousedown', handleClick, false);
    return () => document.removeEventListener('mousedown', handleClick, false);
  }, [onClose, isOpen, btnRef]);

  const isSelected = (route: string) => {
    const trimmedRoute = route.replace(/^\/|\/$/g, '');
    return pathname.includes(trimmedRoute);
  };
  const {
    state: { isAdmin, isCurator, isCuratorAdmin },
  } = useSession();
  return (
    <SideDrawerStyle
      className={`${!isOpen && 'hidden'} ${mobile && 'mobile'}`}
      ref={drawerRef}
    >
      <DrawerSection
        title={formatMessage(messages.system)}
        items={[
          {
            icon: { name: 'users', color: primary.main },
            isSelected: isSelected(ROUTES.ADMIN_MANAGE_USERS),
            text: formatMessage(messages.users),
            to: ROUTES.ADMIN_MANAGE_USERS,
            isHidden: !(isCuratorAdmin || isAdmin),
          },
          {
            icon: { name: 'sitemap', color: primary.main },
            isSelected: isSelected(ROUTES.ACCOUNTS),
            text: formatMessage(messages.accounts),
            to: ROUTES.ACCOUNTS,
          },
          {
            icon: { name: 'sliders', color: primary.main },
            isSelected: isSelected(ROUTES.ADMIN_MANAGE_CONFIGURATIONS),
            text: formatMessage(messages.configurations),
            to: ROUTES.ADMIN_MANAGE_CONFIGURATIONS,
            isHidden: !isAdmin,
          },
          {
            icon: { name: 'plug', color: primary.main },
            isSelected: isSelected(ROUTES.INTEGRATIONS),
            text: formatMessage(messages.integrations),
            to: ROUTES.INTEGRATIONS,
            isHidden: !(isCuratorAdmin || isAdmin || isCurator),
          },
        ]}
      />
      {isAdmin && (
        <DrawerSection
          title={formatMessage(messages.iam)}
          items={[
            {
              icon: { name: 'user-lock', color: primary.main },
              isSelected: isSelected(ROUTES.ADMIN_MANAGE_ROLES),
              text: formatMessage(messages.roles),
              to: ROUTES.ADMIN_MANAGE_ROLES,
            },
            {
              icon: { name: 'arrow-down-up-lock', color: primary.main },
              isSelected: isSelected(ROUTES.ADMIN_MANAGE_OPERATIONS),
              text: formatMessage(messages.operations),
              to: ROUTES.ADMIN_MANAGE_OPERATIONS,
            },
            {
              icon: { name: 'shield-halved', color: primary.main },
              isSelected: isSelected(ROUTES.ADMIN_MANAGE_POLICIES),
              text: formatMessage(messages.policies),
              to: ROUTES.ADMIN_MANAGE_POLICIES,
            },
            {
              icon: { name: 'key', color: primary.main },
              isSelected: isSelected(ROUTES.ADMIN_MANAGE_PERMISSIONS),
              text: formatMessage(messages.permissions),
              to: ROUTES.ADMIN_MANAGE_PERMISSIONS,
            },
          ]}
        />
      )}
    </SideDrawerStyle>
  );
}

export default AdminDrawer;
